import React, { ReactNode } from 'react';
import { cn } from '../utils';

export interface AuthFooterLink {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface AuthLayoutProps {
  children: ReactNode;

  // Layout variants
  variant?: 'centered' | 'fullscreen' | 'split';

  // Branding
  logo?: ReactNode;
  appName?: string;
  tagline?: string;

  // Background
  background?: 'gradient' | 'image' | 'solid';
  backgroundSrc?: string;
  backgroundOverlay?: boolean;

  // Footer
  showFooter?: boolean;
  footerLinks?: AuthFooterLink[];

  // Styling
  className?: string;
  contentClassName?: string;
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  variant = 'centered',
  logo,
  appName,
  tagline,
  background = 'gradient',
  backgroundSrc,
  backgroundOverlay = false,
  showFooter = false,
  footerLinks = [],
  className = '',
  contentClassName = '',
}) => {
  // Background classes
  const backgroundClasses = {
    gradient: 'bg-gradient-to-br from-primary/10 via-base-100 to-secondary/10',
    solid: 'bg-base-100',
    image: backgroundSrc ? '' : 'bg-base-100',
  };

  // Variant-specific classes
  const variantClasses = {
    centered: 'flex items-center justify-center min-h-screen p-4',
    fullscreen: 'flex flex-col min-h-screen',
    split: 'flex min-h-screen', // For future implementation
  };

  const renderBranding = () => {
    if (!logo && !appName && !tagline) return null;

    return (
      <div className="text-center mb-6">
        {logo && <div className="flex justify-center mb-4">{logo}</div>}
        {appName && <h1 className="text-2xl font-bold text-base-content mb-2">{appName}</h1>}
        {tagline && <p className="text-base-content/70">{tagline}</p>}
      </div>
    );
  };

  const renderFooter = () => {
    if (!showFooter || footerLinks.length === 0) return null;

    return (
      <footer className="text-center py-6">
        <div className="flex flex-wrap justify-center gap-4 text-sm text-base-content/60">
          {footerLinks.map((link, index) => (
            <React.Fragment key={index}>
              {link.href ? (
                <a
                  href={link.href}
                  className="hover:text-base-content transition-colors"
                  onClick={link.onClick}
                >
                  {link.label}
                </a>
              ) : (
                <button
                  type="button"
                  className="hover:text-base-content transition-colors"
                  onClick={link.onClick}
                >
                  {link.label}
                </button>
              )}
              {index < footerLinks.length - 1 && <span className="text-base-content/30">•</span>}
            </React.Fragment>
          ))}
        </div>
      </footer>
    );
  };

  const renderContent = () => {
    if (variant === 'fullscreen') {
      return (
        <>
          {/* Top spacer for fullscreen */}
          <div className="flex-1 min-h-[20vh]" />

          {/* Branding section */}
          {(logo || appName || tagline) && <div className="px-6">{renderBranding()}</div>}

          {/* Main content */}
          <div className={cn('px-6 pb-safe', contentClassName)}>{children}</div>

          {/* Footer */}
          {renderFooter()}

          {/* Bottom spacer */}
          <div className="pb-safe" />
        </>
      );
    }

    // Centered variant
    return (
      <div className={cn('w-full max-w-md mx-auto', contentClassName)}>
        {renderBranding()}
        {children}
        {renderFooter()}
      </div>
    );
  };

  return (
    <div
      className={cn(
        variantClasses[variant],
        backgroundClasses[background],
        'relative overflow-hidden',
        className
      )}
      style={
        background === 'image' && backgroundSrc
          ? {
              backgroundImage: `url(${backgroundSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      {/* Background overlay for image/video backgrounds */}
      {backgroundOverlay && background === 'image' && backgroundSrc && (
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      )}

      {/* Content */}
      {variant === 'centered' ? (
        <div className="relative z-10 flex items-center justify-center w-full">
          {renderContent()}
        </div>
      ) : (
        <div className="relative z-10 w-full">{renderContent()}</div>
      )}
    </div>
  );
};
